import { Search } from '@mui/icons-material';
import { Box, Grid, InputAdornment, SvgIcon } from '@mui/material';
import { getQBAlerts } from 'api/adminHub';
import { getAllProjects } from 'api/index';
import type { Project } from 'api/types';
import Can from 'components/Can';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import CustomTextfield from 'components/NewLayout/Textfield';
import PaperContainer from 'components/PaperContainer';
import Tabs from 'components/Tabs';
import debounce from 'lodash/debounce';
import { QBBadge } from 'pages/AdminHub/Heading';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminWorkspaceStore, useCommonStore } from 'store/index';
import { sortArrByKey, tabTitle } from 'utils/index';
import OutstandingBills from './component/OutstandingBills';
import PaymentHistory from './component/PaymentHistory';

export default function PaymentWorkspace() {
  tabTitle('Payment Workspace');
  const setComponentHeading = useCommonStore((state) => state.setComponentHeading);
  const qbAlerts = useAdminWorkspaceStore((state) => state.qbAlerts);
  const setQBAlerts = useAdminWorkspaceStore((state) => state.setQBAlerts);
  const [allProjects, setAllProjects] = useState<DropdownOptionType[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('OutstandingBills');
  const [lastSearches, setLastSearches] = useState<{ [key: string]: string }>({
    OutstandingBills: '',
    PaymentHistory: ''
  });
  const navigate = useNavigate();

  // Use ref to maintain debounced function reference
  const debouncedSearchRef = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    setComponentHeading('Payment Workspace');
    fetchProjects();
    getQBAlerts().then((res) => setQBAlerts(res ?? []));
  }, []);

  // Create debounced function only once and store in ref
  useEffect(() => {
    debouncedSearchRef.current = debounce((value: string, tabKey: string) => {
      // Only update if value is different from current searchText
      if (value !== searchText) {
        setSearchText(value);
        setLastSearches((prev) => ({
          ...prev,
          [tabKey]: value
        }));
      }
    }, 500);

    // Cleanup on unmount
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [searchText]); // Add searchText as dependency to avoid stale closure

  const billAlerts = qbAlerts
    ?.filter(
      (a) => a.action_type?.includes('UPDATE_BILL') || a.action_type?.includes('CREATE_BILL')
    )
    .sort((a, b) => new Date(b.last_updated_at).getTime() - new Date(a.last_updated_at).getTime());

  const handleSearch = (value: string) => {
    // Ensure value is never undefined/null
    const cleanValue = value || '';
    setInputValue(cleanValue);

    // Cancel previous debounced call
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current.cancel();
      debouncedSearchRef.current(cleanValue, activeTab);
    }
  };

  const handleTabChange = (key: string) => {
    // Cancel any pending search when switching tabs
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current.cancel();
    }

    setActiveTab(key);
    const savedSearch = lastSearches[key] || '';
    setInputValue(savedSearch);
    setSearchText(savedSearch);
  };

  const fetchProjects = () => {
    getAllProjects()
      .then((projects) => {
        const sortedProjects: Project[] = sortArrByKey(
          projects
            .filter((p) => p.appearance_order_and_logistic)
            .map((p) => ({ ...p, project_code: parseInt(p.project_code) })),
          'project_code'
        );

        setAllProjects(
          sortedProjects.map((item) => ({
            id: item.project_id ?? '',
            value: `${item.project_code} - ${item.project_name}`
          }))
        );
      })
      .catch((e) => {
        console.error(`Error: ${e.name} - ${e.message}`);
      });
  };

  const tabsData = [
    {
      label: 'Outstanding Bills',
      key: 'OutstandingBills',
      index: '0',
      component: (
        <OutstandingBills
          searchText={searchText}
          key={`outstanding-${searchText}-${activeTab}`}
          allProjects={allProjects}
          qbAlerts={qbAlerts}
        />
      )
    },
    {
      label: 'Payment History',
      key: 'PaymentHistory',
      index: '1',
      component: (
        <PaymentHistory
          searchText={searchText}
          key={`history-${searchText}-${activeTab}`}
          allProjects={allProjects}
          qbAlerts={qbAlerts}
        />
      )
    }
  ];

  return (
    <PaperContainer>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={4}>
          <CustomTextfield
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon component={Search} />
                </InputAdornment>
              )
            }}
            name="search"
            labelText="Search"
            placeholder="Search something"
            value={inputValue}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
            <Can I="read" module="QuickBooksAlert">
              <QBBadge
                count={billAlerts.length}
                onClickQBAlerts={() =>
                  navigate('/admin-hub/integration-workspace', { state: { openSection: 5 } })
                }
              />
            </Can>
          </Box>
        </Grid>
      </Grid>

      <Tabs tabs={tabsData} onChange={handleTabChange} activeTab={activeTab} />
    </PaperContainer>
  );
}
